import React, { type KeyboardEvent, useCallback, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { MenuGroup } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { SideNavMenuItem } from '@atlassian/conversation-assistant-ui-components/ui/SideNavMenuItem';

import {
	type AgentTabs,
	type BrowseCategory,
	type CustomTabsConfig,
	type UIContext,
} from '../../../common/types.ts';
import { TabButton } from '../../../common/ui/tab-button/index.tsx';
import { useAgentOverrider } from '../../../controllers/agent-overrider/index.tsx';
import { AGENTS_CONTAINER_ID } from '../../../controllers/agents/constants.ts';
import { useBrowseAgentsUIStore } from '../../../controllers/browse-agents-ui/index.tsx';

import messages from './messages.ts';

const styles = cssMap({
	sideBarAgentsTabsRefresh: {
		position: 'relative',
		width: '100%',
		paddingLeft: token('space.200'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderColor: token('color.border'),
	},
	modalAgentsTabs: {
		flex: 1,
		paddingTop: token('space.100'),
	},
});

const defaultAgentTabs: AgentTabs[] = ['all-agents', 'starred-agents', 'my-agents'];

type Props = {
	agentTabs?: AgentTabs[];
	customTabs?: CustomTabsConfig;
	viewContext: UIContext;
	siteId: string;
	onTabChange: (tab: BrowseCategory) => void;
	selectedTab: BrowseCategory;
};

export const BrowseAgentTabs = ({
	agentTabs: agentTabsProp,
	customTabs,
	viewContext,
	onTabChange,
	selectedTab,
}: Props): JSX.Element => {
	di(useAnalytics, useAnalyticsEvents, useIntl, useAgentOverrider, useBrowseAgentsUIStore);
	const { sendAnalyticsEvent } = useAnalytics();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { agentIdFiltersOverride } = useAgentOverrider();

	const agentTabs = useMemo((): AgentTabs[] => {
		if (agentTabsProp) {
			return agentTabsProp;
		}
		if (fg('rovo_agents_agent_verification')) {
			return [...defaultAgentTabs, 'verified-agents'];
		}
		return defaultAgentTabs;
	}, [agentTabsProp]);

	const filteredAgentTabs = useMemo(() => {
		let result: Array<{ label: string; value: BrowseCategory }> = [];

		// Force to only "All Agents" tab if there are any overridden agents
		if (agentIdFiltersOverride?.length) {
			result.push({
				label: formatMessage(messages.allAgentsButton),
				value: 'all-agents',
			});

			return result;
		}

		agentTabs.forEach((tab) => {
			const customTab = customTabs?.[tab];
			if (customTab) {
				result.push({
					label: customTab.label,
					value: tab,
				});
				return;
			}

			switch (tab) {
				case 'all-agents':
					result.push({
						label: formatMessage(messages.allAgentsButton),
						value: 'all-agents',
					});
					break;
				case 'starred-agents':
					result.push({
						label: formatMessage(messages.starredAgentsButton),
						value: 'starred-agents',
					});
					break;
				case 'my-agents':
					result.push({
						label: formatMessage(messages.myAgentsButton),
						value: 'my-agents',
					});
					break;
				case 'templates': {
					result.push({
						label: formatMessage(messages.templatesButton),
						value: 'templates' as const,
					});
					break;
				}
				case 'verified-agents':
					result.push({
						label: FeatureGates.getExperimentValue(
							'jira_agent_recommendations_m1',
							'isEnabled',
							false,
						)
							? formatMessage(messages.verifiedAgentsButtonM1)
							: formatMessage(messages.verifiedAgentsButton),
						value: 'verified-agents',
					});
					break;
				default:
					break;
			}
		});

		return result;
	}, [agentTabs, customTabs, formatMessage, agentIdFiltersOverride]);

	const [_, { setSearchValue }] = useBrowseAgentsUIStore();

	const displayedAgentTabs = useMemo(() => {
		if (viewContext === 'agents-modal') {
			return filteredAgentTabs.filter(({ value }) => value !== 'starred-agents');
		}
		return filteredAgentTabs;
	}, [filteredAgentTabs, viewContext]);

	const handleCategoryChange = useCallback(
		({ value }: { value: BrowseCategory }) => {
			onTabChange(value);
			setSearchValue('');
			sendAnalyticsEvent(
				createAnalyticsEvent({
					actionSubjectId: 'browseAgentsFilterButton',
					attributes: {
						browseCategory: value,
					},
					action: 'clicked',
					actionSubject: 'button',
				}),
			);
		},
		[onTabChange, setSearchValue, sendAnalyticsEvent, createAnalyticsEvent],
	);

	const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

	const handleTabKeyDown = useCallback(
		(event: KeyboardEvent<HTMLButtonElement>, index: number) => {
			const lastIndex = displayedAgentTabs.length - 1;
			let nextIndex: number | null = null;

			switch (event.key) {
				case 'ArrowRight':
					nextIndex = index === lastIndex ? 0 : index + 1;
					break;
				case 'ArrowLeft':
					nextIndex = index === 0 ? lastIndex : index - 1;
					break;
				case 'Home':
					nextIndex = 0;
					break;
				case 'End':
					nextIndex = lastIndex;
					break;
				default:
					return;
			}

			event.preventDefault();
			event.stopPropagation();

			const nextTab = displayedAgentTabs[nextIndex];
			if (!nextTab) {
				return;
			}
			tabRefs.current[nextIndex]?.focus();
			handleCategoryChange({ value: nextTab.value });
		},
		[displayedAgentTabs, handleCategoryChange],
	);

	if (viewContext === 'agents-modal') {
		return (
			<Stack role="tablist" xcss={styles.modalAgentsTabs}>
				{displayedAgentTabs.map(({ value, label }, i) => (
					<MenuGroup spacing="compact" key={i}>
						<SideNavMenuItem
							role="tab"
							title={label}
							isSelected={selectedTab === value}
							onClick={() =>
								handleCategoryChange({
									value,
								})
							}
						></SideNavMenuItem>
					</MenuGroup>
				))}
			</Stack>
		);
	}

	return (
		<Inline alignBlock="center" space="space.200">
			<Inline role="tablist" alignBlock="center" xcss={styles.sideBarAgentsTabsRefresh}>
				{displayedAgentTabs.map(({ value, label }, index) => (
					<TabButton
						isSelected={selectedTab === value}
						key={label}
						onClick={() =>
							handleCategoryChange({
								value,
							})
						}
						{...(fg('jfp_a11y_team_browse_agents_tabpanel') && {
							'aria-controls': AGENTS_CONTAINER_ID,
							id: `tab-${value}`,
						})}
						ref={(node) => (tabRefs.current[index] = node)}
						tabIndex={selectedTab === value ? 0 : -1}
						onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => handleTabKeyDown(event, index)}
					>
						{label}
					</TabButton>
				))}
			</Inline>
		</Inline>
	);
};
